import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { lifecycleService } from "./lifecycle.service";
import type {
	JobStatusLifecyclesQueryParams,
	StatusLifecyclesQueryInput,
	StatusLifecyclesQueryParams,
	TicketStatusLifecyclesQueryParams,
} from "./lifecycle.types";

export const lifecycleKeys = {
	all: ["lifecycles"] as const,
	lists: () => [...lifecycleKeys.all, "list"] as const,
	statusLifecycles: (params?: StatusLifecyclesQueryParams) =>
		[...lifecycleKeys.all, "status-lifecycles", params] as const,
	statusLifecyclesPage: (
		start: number,
		count: number,
		params?: StatusLifecyclesQueryInput,
	) => [...lifecycleKeys.all, "page", start, count, params] as const,
	statusLifecyclesCount: (params?: StatusLifecyclesQueryInput) =>
		[...lifecycleKeys.all, "count", params] as const,
	statusLifecycleDetail: (id: string, organizationId?: string) =>
		[...lifecycleKeys.all, "detail", id, organizationId] as const,
	statusCategoryTypes: () =>
		[...lifecycleKeys.all, "entity-type-classes"] as const,
	jobStatusLifecycles: (params?: JobStatusLifecyclesQueryParams) =>
		[...lifecycleKeys.all, "job-status-lifecycles", params] as const,
	ticketStatusLifecycles: (params?: TicketStatusLifecyclesQueryParams) =>
		[...lifecycleKeys.all, "ticket-status-lifecycles", params] as const,
	statusLifecycleHistory: (id: string, organizationId?: string) =>
		[...lifecycleKeys.all, "history", id, organizationId] as const,
	statusLifecycleHistoryCount: (id: string, organizationId?: string) =>
		[
			...lifecycleKeys.statusLifecycleHistory(id, organizationId),
			"count",
		] as const,
	statusLifecycleHistoryList: (
		id: string,
		start: number,
		count: number,
		organizationId?: string,
	) =>
		[
			...lifecycleKeys.statusLifecycleHistory(id, organizationId),
			"list",
			start,
			count,
		] as const,
} as const;

export const lifecycleQueryOptions = {
	/**
	 * Legacy full list — count + paginated fetch (replaces flat GET).
	 */
	statusLifecycles: (params?: StatusLifecyclesQueryParams) =>
		queryOptions({
			queryKey: lifecycleKeys.statusLifecycles(params),
			queryFn: () => lifecycleService.getStatusLifecycles(params),
			staleTime: 5 * 60 * 1000,
			gcTime: 10 * 60 * 1000,
		}),

	/**
	 * GET /workflows/status-lifecycles/{start}/{count}
	 */
	statusLifecyclesPage: (
		start: number,
		count: number,
		params?: StatusLifecyclesQueryInput,
	) =>
		queryOptions({
			queryKey: lifecycleKeys.statusLifecyclesPage(start, count, params),
			queryFn: () =>
				lifecycleService.getStatusLifecyclesPage(start, count, params),
			staleTime: 5 * 60 * 1000,
			gcTime: 10 * 60 * 1000,
		}),

	/**
	 * GET /workflows/status-lifecycles/count
	 */
	statusLifecyclesCount: (params?: StatusLifecyclesQueryInput) =>
		queryOptions({
			queryKey: lifecycleKeys.statusLifecyclesCount(params),
			queryFn: () => lifecycleService.getStatusLifecyclesCount(params),
			staleTime: 0,
			gcTime: 5 * 60 * 1000,
		}),

	/**
	 * GET /workflows/status-lifecycles/{id}
	 */
	statusLifecycleDetail: (id: string, organizationId?: string) =>
		queryOptions({
			queryKey: lifecycleKeys.statusLifecycleDetail(id, organizationId),
			queryFn: () =>
				lifecycleService.getStatusLifecycleById(id, { organizationId }),
			staleTime: 5 * 60 * 1000,
			gcTime: 10 * 60 * 1000,
			enabled: !!id && id.length > 0,
		}),
	statusCategoryTypes: () =>
		queryOptions({
			queryKey: lifecycleKeys.statusCategoryTypes(),
			queryFn: () => lifecycleService.getStatusCategoryTypes(),
			staleTime: 30 * 60 * 1000,
			gcTime: 60 * 60 * 1000,
		}),

	/**
	 * GET /jobs/status-lifecycles
	 */
	jobStatusLifecycles: (params?: JobStatusLifecyclesQueryParams) =>
		queryOptions({
			queryKey: lifecycleKeys.jobStatusLifecycles(params),
			queryFn: () => lifecycleService.getJobStatusLifecycles(params),
			staleTime: 5 * 60 * 1000,
			gcTime: 10 * 60 * 1000,
		}),
	/**
	 * GET /tickets/status-lifecycles
	 */
	ticketStatusLifecycles: (params?: TicketStatusLifecyclesQueryParams) =>
		queryOptions({
			queryKey: lifecycleKeys.ticketStatusLifecycles(params),
			queryFn: () => lifecycleService.getTicketStatusLifecycles(params),
			staleTime: 5 * 60 * 1000,
			gcTime: 10 * 60 * 1000,
		}),

	history: {
		/**
		 * GET /workflows/{id}/history/count
		 */
		count: (id: string, organizationId?: string) =>
			queryOptions({
				queryKey: lifecycleKeys.statusLifecycleHistoryCount(id, organizationId),
				queryFn: () =>
					lifecycleService.getWorkflowHistoryCount(id, { organizationId }),
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled: !!id && id.length > 0,
			}),

		/**
		 * GET /workflows/status-lifecycles/{id}/history/{start}/{count}
		 */
		list: (
			id: string,
			start: number = 1,
			count: number = 10,
			organizationId?: string,
		) =>
			queryOptions({
				queryKey: lifecycleKeys.statusLifecycleHistoryList(
					id,
					start,
					count,
					organizationId,
				),
				queryFn: () =>
					lifecycleService.getStatusLifecycleHistory(id, start, count, {
						organizationId,
					}),
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled: !!id && id.length > 0,
			}),

		/**
		 * Infinite query for lifecycle history (newest first)
		 */
		infinite: (
			id: string,
			pageSize: number = 10,
			totalCount: number,
			organizationId?: string,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...lifecycleKeys.statusLifecycleHistoryList(
						id,
						1,
						pageSize,
						organizationId,
					),
					"infinite",
					totalCount,
				],
				queryFn: ({ pageParam }) =>
					lifecycleService.getStatusLifecycleHistory(id, pageParam, pageSize, {
						organizationId,
					}),
				initialPageParam: totalCount
					? Math.max(1, totalCount - pageSize + 1)
					: 1,
				getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
					if (lastPageParam <= 1) {
						return undefined;
					}
					return Math.max(1, lastPageParam - pageSize);
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (!totalCount || firstPageParam >= totalCount - pageSize + 1) {
						return undefined;
					}
					return firstPageParam + pageSize;
				},
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled: !!id && id.length > 0 && totalCount !== undefined,
			}),
	},
};
